import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ListItem } from '../../types/list';
import { useImageDisplay } from '../../hooks/useImageDisplay';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useSection } from '../../hooks/useSection';
import { GiftIcon } from '../icons';

interface ListViewProps {
  items: ListItem[];
  selectedCategory: string;
  onItemClick: (item: ListItem) => void;
  onCategorySelect: (category: string) => void;
  config: any;
  translations: Record<string, Record<string, string>>;
  categoryColors: Map<string, string>;
}

export const ListGrid: React.FC<ListViewProps> = ({
  items,
  selectedCategory,
  onItemClick,
  onCategorySelect,
  config,
  translations,
  categoryColors
}) => {
  const { i18n } = useTranslation();
  const section = useSection();
  
  // Initialize analytics with section context
  const analytics = useAnalytics({
    sectionId: section.id,
    sectionName: section.names.fr,
    sectionType: 'liste'
  });

  const getTranslatedCategory = (category: string): string => {
    const translation = translations[category];
    if (!translation) return category;
    return translation[i18n.language] || translation.fr || category;
  };

  // Filtrer les items selon la catégorie sélectionnée
  const filteredItems = React.useMemo(() => {
    if (!selectedCategory) return items;
    return items.filter(item => item.category === selectedCategory);
  }, [items, selectedCategory]);

  // Créer un composant séparé pour chaque item
  const ListItemCard = React.memo(({ item }: { item: ListItem }) => {
    const { displayImage, shouldDisplay } = useImageDisplay(item.image, 'listCard');

    const handleItemClick = () => {
      analytics.trackItemAction(item.id, 'item_click', 'liste', {
        item_name: item.name,
        category: item.category,
        type: item.type,
        source: 'list_card'
      });
      onItemClick(item);
    };

    const handleCategoryClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      analytics.trackFilter('category_filter', item.category);
      onCategorySelect(item.category);
    };

    return (
      <div
        className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200 relative"
        style={{ borderRadius: config.border_radius }}
      >
        {/* Gift icon for items with offers */}
        {item.offer && (
          <div 
            className="absolute top-0 right-0 w-10 h-10 flex items-center justify-center z-10"
            style={{ 
              backgroundColor: config['major-color'],
              borderTopRightRadius: config.border_radius,
              borderBottomLeftRadius: config.border_radius
            }}
          >
            <GiftIcon size={20} className="text-white" />
          </div>
        )}
        
        <div 
          className="cursor-pointer"
          onClick={handleItemClick}
        >
          {shouldDisplay && displayImage && (
            <div className="relative pb-[66.666%]">
              <img
                src={displayImage}
                alt={item.name}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          )}
          
          <div className="p-4">
            <h3 className="text-lg font-medium mb-2">{item.name}</h3>
            
            {item.fulladress && (
              <p className="text-sm text-gray-600 mb-2 truncate max-w-[calc(100%-10px)] sm:max-w-[200px] md:max-w-[250px] lg:max-w-[300px] xl:max-w-full">
                {item.fulladress}
              </p>
            )}

            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onCategorySelect(item.category);
                }}
                className="inline-block px-1.5 py-0.5 text-xs rounded hover:opacity-80 transition-opacity text-white"
                style={{ backgroundColor: categoryColors.get(item.category) || config['medium-color'] }}
              >
                {getTranslatedCategory(item.category)}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  });

  if (!filteredItems || filteredItems.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <p className="text-gray-500">Aucun élément disponible</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredItems.map((item) => (
          <ListItemCard key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
};